/**
 * SetDetailPage — 세트 상세 · 카드 목록 (PKA 화면정의서 SC-006).
 *
 * 필터·정렬은 벤치마크와 같은 구조로 **칩을 누르면 바텀시트가 열린다** —
 * 화면정의서가 SC-006a~f 를 전부 '바텀시트' 화면유형으로 정의하고 있다.
 *
 * ⚠ **여섯 축 중 셋은 데이터가 없다.** 화면정의서는 SC-006b 유형(포켓몬/트레이너/에너지)·
 * SC-006c Finish·SC-006e 에너지를 요구하지만 `cd_card_mst` 에 해당 컬럼이 없고
 * `CARD_DTL_JSON` 도 전량 null 이다(2026-08-05 실측). 수집서버가 카드 능력치·표면 정보를
 * 주지 않는다. 그래서 지금 거는 축은 **가격 범위·희귀도·정렬** 셋뿐이고, 나머지는
 * "준비 중"으로 밝힌다. 옵션이 없는 필터 칩을 띄우면 눌러도 아무 일이 없는 함정이 된다.
 *
 * ⚠ 필터·정렬은 전부 **서버**가 건다. 페이지 단위로 받아 앱에서 거르면 결과 수가 스크롤할
 * 때마다 늘어난다(`useCardSearchQuery` 주석 참조).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { track } from "../../lib/tracking";
import { useTranslation } from "react-i18next";
import { AspectRatio } from "@astryxdesign/core/AspectRatio";
import { Button } from "@astryxdesign/core/Button";
import { ClickableCard } from "@astryxdesign/core/ClickableCard";
import { Divider } from "@astryxdesign/core/Divider";
import { DropdownMenu } from "@astryxdesign/core/DropdownMenu";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Grid } from "@astryxdesign/core/Grid";
import InFeedAd from "../../components/ads/InFeedAd";
import { AD_DENSITY_SET_CARDS, withInFeedAds } from "../../components/ads/adPlacement";
import { Heading } from "@astryxdesign/core/Heading";
import { Link } from "@astryxdesign/core/Link";
import { Item } from "@astryxdesign/core/Item";
import { List } from "@astryxdesign/core/List";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { Section } from "@astryxdesign/core/Section";
import { SegmentedControl, SegmentedControlItem } from "@astryxdesign/core/SegmentedControl";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Text } from "@astryxdesign/core/Text";
import { ToggleButton } from "@astryxdesign/core/ToggleButton";
import { Token } from "@astryxdesign/core/Token";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import LazyImage from "../../components/primitives/LazyImage";
import Sheet from "../../components/feedback/Sheet";
import { useMoney } from "../../hooks/useMoney";
import { cardImageUrl, type CardItem, type CardSortKey } from "../../api/card";
import {
  useCardSearchQuery,
  useCardsetDetailQuery,
  useCardsetPriceSummary,
  flattenCards,
} from "../../hooks/useCardQueries";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useAuthStore } from "../../store/authStore";
import { maskPrice } from "../../lib/priceMasking";
import { useOverlayNavigate } from "../../hooks/useOverlayNavigate";
import CardThumb from "../../components/card/CardThumb";
import SetLogo from "../../components/card/SetLogo";
import AppInstallBanner from "../../components/web/AppInstallBanner";
import Icon from "../../components/primitives/Icon";
import { useCollectionQuery } from "../../hooks/useCollectionQueries";
import styles from "./SetDetailPage.module.css";

/** SC-006f 정렬. 희귀도·HP 는 데이터가 없어 빠졌다 — `CardSortKey` 주석 참조. */
const SORT_KEYS: CardSortKey[] = ["NUMBER", "NAME", "PRICE_DESC", "PRICE_ASC"];
const DEFAULT_SORT: CardSortKey = "NUMBER";

/** 칩을 눌러 여는 바텀시트. */
/** 바텀시트를 여는 필터들. 정렬은 드롭다운이라 여기 없다 (design 2026-08-20). */
type FilterKind = "price" | "rarity";

/** SC-006a 가격 범위 프리셋 — 원화 기준으로 재정의 (벤치마크는 $0-1/$1-10/…). */
/**
 * 앨범형 셀의 최소 폭 — 모바일 2열 (design 2026-08-18. 그전에는 3열이었다).
 *
 * ⚠ **본문 폭에서 역산한 값이다.** 잘못 잡으면 열이 조용히 하나 늘거나 준다.
 *   375 기준 본문 = 375 − 24×2 = 327, gap={3} = 12.
 *     2열이 되려면 (327 − 12) / 2 = 157 이하 **이면서** (327 − 24) / 3 = 101 초과 → 150
 *   페이지 거터나 gap 을 바꾸면 이 숫자도 다시 계산해야 한다.
 */
const ALBUM_MIN_WIDTH = 150;

const PRICE_BANDS: { key: string; min: number; max?: number }[] = [
  { key: "u10k", min: 0, max: 10_000 },
  { key: "10k50k", min: 10_000, max: 50_000 },
  { key: "50k200k", min: 50_000, max: 200_000 },
  { key: "o200k", min: 200_000 },
];

/**
 * @param cardsetSerialNo ActivityHost 전용 — activity 안에서는 라우트 매치가 없어
 *   useParams 가 비므로 resolver 가 URL 에서 파싱해 넘긴다. 라우트 렌더에서는 생략.
 */
export default function SetDetailPage({
  cardsetSerialNo: serialNoProp,
}: { cardsetSerialNo?: string } = {}) {
  const { t } = useTranslation("card");
  const money = useMoney();
  const overlayNavigate = useOverlayNavigate();
  // 컬렉션은 메인 탭이라 activity 스택이 아니라 탭 전환이다.
  const navigate = useNavigate();
  const { cardsetSerialNo: paramSerialNo } = useParams<{ cardsetSerialNo: string }>();
  const rawSerialNo = serialNoProp ?? paramSerialNo;
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);

  // 더미 시절의 문자열 uid 링크(홈·검색 이전 버전)가 아직 남아 있다.
  const cardsetSerialNo =
    rawSerialNo && /^\d+$/.test(rawSerialNo) ? Number(rawSerialNo) : null;

  const [sort, setSort] = useState<CardSortKey>(DEFAULT_SORT);
  /**
   * 앨범형 ↔ 리스트형 (design 2026-08-18). 종전의 2열/3열 토글을 대신한다.
   * 3열은 카드 한 장이 96px 이라 이름이 거의 다 잘렸다 — 카드 그림을 보려고 켜는
   * 뷰인데 그림이 손톱만 했다. 열 수 대신 "격자냐 줄이냐"를 고르게 한다.
   */
  const [viewMode, setViewMode] = useState<"album" | "list">("album");
  const [openSheet, setOpenSheet] = useState<FilterKind | null>(null);
  const [rarities, setRarities] = useState<string[]>([]);
  /** 단일 선택 — 겹치는 구간을 동시에 고르면 서버에 보낼 min/max 가 정해지지 않는다. */
  const [band, setBand] = useState<string | null>(null);

  // 헤더 '보유' — 컬렉션의 세트별 진행률에서 온다. 비로그인은 훅이 호출되지 않아
  // undefined 가 되고, 화면은 0 이 아니라 '-' 로 둔다(모르는 것과 없는 것은 다르다).
  const collection = useCollectionQuery();
  const ownedCardCount =
    collection.data?.setProgress.find((p) => p.cardsetSerialNo === cardsetSerialNo)
      ?.ownedCardCount ?? null;

  const setDetail = useCardsetDetailQuery(cardsetSerialNo);
  const cardset = setDetail.data?.cardset;

  /**
   * 세트 상세 1건 — 언어를 같이 남기려고 **응답이 온 뒤** 한 번 쏜다(마운트 시점엔
   * cardset 이 없다). 세트당 1회는 ref 가드가 지킨다.
   */
  const setViewTrackedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!cardset || cardsetSerialNo === null) return;
    if (setViewTrackedRef.current === cardsetSerialNo) return;
    setViewTrackedRef.current = cardsetSerialNo;
    track("set_detail_view", {
      cardsetSerialNo,
      language: cardset.language,
    });
  }, [cardset, cardsetSerialNo]);

  const priceRange = useMemo(() => {
    const found = PRICE_BANDS.find((b) => b.key === band);
    return { minPrice: found?.min, maxPrice: found?.max };
  }, [band]);

  const cards = useCardSearchQuery({
    cardsetSerialNo: cardsetSerialNo ?? undefined,
    rarities,
    ...priceRange,
    sort,
  });

  const visible = flattenCards(cards.data);

  /**
   * 헤더 평균가 — 세트 카드를 전량 받아 앱에서 낸다(BE 집계가 없다).
   * ⚠ `cards` 가 아니라 **별도 쿼리**다. 목록에는 필터가 걸려 있다 — 훅 주석 참조.
   */
  const priceSummary = useCardsetPriceSummary(cardsetSerialNo);

  const sentinelRef = useInfiniteScroll({
    enabled: !!cards.hasNextPage && !cards.isFetchingNextPage,
    onIntersect: () => void cards.fetchNextPage(),
  });

  const toggleRarity = (value: string) =>
    setRarities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  if (setDetail.isPending && cardsetSerialNo !== null) {
    return (
      <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
        <VStack gap={4} aria-hidden="true">
          <Skeleton height={120} />
          <Skeleton height={40} />
          <Skeleton height={300} />
        </VStack>
      </Section>
    );
  }

  if (!cardset) {
    return (
      <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
        <VStack gap={3} align="center">
          <Text color="secondary" justify="center">{t("set.notFound")}</Text>
          {setDetail.isError && (
            <Button
              variant="secondary"
              label={t("search.retry")}
              clickAction={() => void setDetail.refetch()}
            />
          )}
        </VStack>
      </Section>
    );
  }

  const rarityOptions = setDetail.data?.rarities ?? [];

  const sheetTitle: Record<FilterKind, string> = {
    price: t("set.filterPrice"),
    rarity: t("set.filterRarity"),
  };

  /** 필터 칩 — 선택 개수를 배지로 달아 시트를 열지 않고도 걸린 조건이 보이게 한다. */
  /**
   * 필터 여는 버튼 — **secondary 버튼**이다 (design 2026-08-14).
   * 예전엔 ToggleButton(칩)이었는데 테두리도 배경도 없어 글자처럼 보였다.
   * 누를 수 있다는 신호가 없으면 필터가 있는 줄도 모른다.
   *
   * ⚠ '선택 개수 배지'는 뗐다 — 걸린 조건은 아래 결과 수·초기화 줄이 말하고,
   *   같은 사실을 숫자와 버튼으로 두 번 말할 이유가 없다.
   */
  const filterButton = (kind: FilterKind, label: string) => (
    <Button
      key={kind}
      variant="secondary"
      size="sm"
      /* 정렬 드롭다운과 **한 줄에 선다** — 한쪽만 32px 로 내리면 줄이 들쭉날쭉해진다.
         이 줄은 전부 되돌릴 수 있는 동작이라 같이 내린다 (global.css 의 data-compact 참조). */
      data-compact=""
      label={label}
      clickAction={() => setOpenSheet(kind)}
    />
  );

  /**
   * 지금 걸려 있는 조건들 — 값 하나에 칩 하나. 누르면 그 하나만 풀린다 (design 2026-08-19).
   * 필터 버튼 줄 **아래**에 둔다: 위는 "무엇으로 거를까"(입구), 아래는 "지금 무엇이
   * 걸려 있나"(상태)라 섞으면 둘 다 안 읽힌다.
   * ⚠ dev 의 필터 축은 레어도·가격대 둘뿐이다 — design 의 유형·표면·에너지는
   *   카탈로그에 컬럼이 없어 필터 자체가 없다.
   */
  const activeChips: { key: string; label: string; clear: () => void }[] = [
    ...(band
      ? [{
          key: `band:${band}`,
          label: t(`set.band.${band}`),
          clear: () => setBand(null),
        }]
      : []),
    ...rarities.map((v) => ({
      key: `rarity:${v}`,
      label: v,
      clear: () => setRarities((prev) => prev.filter((x) => x !== v)),
    })),
  ];

  const renderCell = (card: CardItem) => {
    const price = card.listPrice;
    const hasPrice = price.display !== "NONE" && price.amount !== null;
    const imageUrl = cardImageUrl(card.imagePathway);
    return (
      <ClickableCard
        key={card.cardSerialNo}
        label={card.name}
        variant="transparent"
        padding={0}
        onClick={() => overlayNavigate(`/cards/${card.cardSerialNo}`)}
      >
        <VStack gap={1}>
          <AspectRatio ratio={63 / 88}>
            {imageUrl ? (
              <LazyImage className={styles.artwork} src={imageUrl} />
            ) : (
              <span className={styles.artworkEmpty} aria-hidden="true" />
            )}
          </AspectRatio>
          {/* 카드명은 body(16px/400) — 홈·컬렉션 앨범형과 같은 스펙이다 (design 2026-08-19).
              ⚠ label 이 아니다. label 은 이 테마에서 16px/500 이라 굵기를 따로 덮어써야
                하고, Astryx 는 "타입이 맞으면 weight 를 덮지 말라"고 한다. */}
          <Text type="body" maxLines={1} wordBreak="break-all">{card.name}</Text>
          {(card.number || card.variantLabel) && (
            <Text type="supporting" color="secondary">
              {[card.number, card.variantLabel].filter(Boolean).join(" · ")}
            </Text>
          )}
          {!hasPrice ? (
            <Text type="label" color="secondary">{t("search.noPrice")}</Text>
          ) : (
            <Text type="label" weight="bold">
              {isAuthenticated
                ? money.format(price.amount ?? 0, price.currency ?? undefined)
                : maskPrice(price.amount ?? 0)}
            </Text>
          )}
        </VStack>
      </ClickableCard>
    );
  };

  return (
    <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
      <VStack gap={5}>
        <AppInstallBanner />
        {/* ── 세트 헤더 ─────────────────────────────────────────────── */}
        <VStack gap={3}>
          <VStack gap={2}>
            {/* 세트 로고 — 제목 위에 둔다. 세트를 알아보는 표지라 글자보다 먼저
                눈에 들어와야 한다. 에셋이 없는 세트는 자리표시자가 보인다. */}
            <div className={styles.setLogoSlot}>
              <SetLogo logoUrl={cardImageUrl(cardset.logoImagePathway) ?? undefined} />
            </div>
            <Heading level={1}>{cardset.name}</Heading>
            <HStack gap={1} wrap="wrap">
              <Token size="sm" label={cardset.code.toUpperCase()} />
              <Token size="sm" color="teal" label={t(`edition.${cardset.language}`)} />
              {/* 시리즈명은 카탈로그에 아직 전량 비어 있다 — 있을 때만 단다 */}
              {cardset.seriesName && (
                <Token size="sm" color="purple" label={cardset.seriesName} />
              )}
            </HStack>
          </VStack>

          {/* 2×2 (design 2026-08-18). columns="multi" 는 폭에 따라 열 수가 바뀌어
              모바일에서 항목이 한 줄씩 세로로 늘어섰다. 고정 집합이라 두 줄로 못 박는
              편이 낫다 — 세로 길이가 절반이 된다.
              ⚠ columns={2} 만으로는 안 바뀐다. Astryx 가 auto-fit 최소폭을 두고 있어
                375px 화면에서는 그냥 1열로 접힌다. 감싼 div 에서 그리드를 다시
                눌러야 한다 — .metaGrid 참조. */}
          <div className={styles.metaGrid}>
            <MetadataList columns={2} label={{ position: "top" }}>
              {/* 평균가는 세트 **전체** 카드의 시세를 모아야 낼 수 있는데, 목록은 페이지
                  단위로만 오고 필터까지 걸려 있다. 그래서 전량을 따로 받아 앱에서 낸다
                  (useCardsetPriceSummary).
                  ⚠ **분모를 함께 적는다.** 시세가 있는 카드만 세는데 세트별 보유율이
                    고르지 않아(ko 세트는 14%) 숫자만 두면 "이 세트 카드 한 장 평균"으로
                    읽힌다. 받는 중에는 그 숫자가 올라가며 로딩 중임을 함께 말한다. */}
              <MetadataListItem label={t("set.avgPrice")}>
                {priceSummary.average === null ? (
                  priceSummary.isComplete ? t("search.noPrice") : t("search.loadingMore")
                ) : (
                  <VStack gap={0}>
                    <Text type="body">
                      {isAuthenticated
                        ? money.format(priceSummary.average, priceSummary.currency ?? undefined)
                        : maskPrice(priceSummary.average)}
                    </Text>
                    <Text type="supporting" color="secondary">
                      {t("set.avgBasis", { count: priceSummary.pricedCount })}
                    </Text>
                  </VStack>
                )}
              </MetadataListItem>
              <MetadataListItem label={t("set.releaseDate")}>
                {cardset.releasedOn?.slice(0, 10).replace(/-/g, ".") ?? "-"}
              </MetadataListItem>
              <MetadataListItem label={t("set.totalCards")}>
                {t("collection.cardCount", { count: cardset.totalCardCount })}
              </MetadataListItem>
              {/* 보유 — 막대가 아니라 숫자만이다 (design 2026-08-14). 대부분의 세트에서
                  보유가 한 자릿수라 막대는 늘 비어 보였다(2/190 이면 1%). 진도를
                  알려주는 대신 "아직 아무것도 안 했다"만 반복해 말하는 셈이었다.
                  분모도 안 적는다 — 바로 위 '수록 카드'가 이미 말한다.

                  네 항목 중 **여기만 누를 수 있다** (design 2026-08-18). 나머지 셋은 이
                  세트의 성질이라 갈 곳이 없지만, 보유는 내 것이라 "그 카드들이 어디
                  있나"라는 다음 질문이 따라붙는다 — 컬렉션으로 보낸다.
                  ⚠ 비로그인은 컬렉션 API 를 부르지 않으므로 0 이 아니라 '-' 다.
                    0 은 "없다"는 사실인데 비로그인은 사실을 모르는 상태다. */}
              <MetadataListItem label={t("set.ownedLabel")}>
                {!isAuthenticated || ownedCardCount === null ? (
                  "-"
                ) : (
                  <Link
                    href="/collections"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/collections");
                    }}
                  >
                    {t("collection.cardCount", { count: ownedCardCount })}
                  </Link>
                )}
              </MetadataListItem>
            </MetadataList>
          </div>
        </VStack>

        <Divider />

        {/* ── 정렬 · 뷰 / 필터 / 걸린 조건 (SC-006a·d·f) ─────────────── */}
        {/* 세 줄로 나눈다 (design 2026-08-28). 전에는 필터가 맨 위, 정렬이 칩 아래라
            **결과가 원인보다 먼저** 왔다. 셋은 하는 일이 다르다:
              ① 정렬 · 뷰 전환 — **목록 전체를 어떻게 볼 것인가.** 늘 하나가
                 켜져 있고 끌 수 없다. 그래서 맨 위, 서로 같은 위계로 마주 본다.
              ② 필터 — **무엇을 뺄 것인가.** 안 걸어도 되고 여러 개 걸 수 있다.
              ③ 걸린 조건 — ②의 **결과**다. 그래서 ② 바로 아래고, 초기화도 여기 붙는다. */}
        <VStack gap={3}>
          {/* ① 정렬 · 뷰 전환 — 마주 보게 둔다. */}
          <HStack gap={2} align="center" justify="between">
            {/* 정렬만 **드롭다운**이다 (design 2026-08-20).
                나머지는 값을 여러 개 고르고 슬라이더까지 있어 바텀시트가 맞지만, 정렬은
                다섯 중 하나를 고르고 끝난다 — 그 하나를 위해 화면 절반을 덮는 시트가
                올라왔다 내려가는 건 과하다. 누른 자리 바로 아래에서 고르고 닫힌다.
                ⚠ hasChevron={false} — 기본 chevron 대신 필터 버튼들과 같은 픽셀
                  Dropdown 아이콘을 쓴다. */}
            <DropdownMenu
              button={{
                label: `${t("set.sortLabel")} · ${t(`set.sort.${sort}`)}`,
                variant: "secondary",
                size: "sm",
                /* 보유 카드(SC-002)의 정렬과 **같은 크기**다 (design 2026-08-28).
                   같은 일을 하는 같은 부품이 화면마다 다르면 안 된다. */
                "data-compact": "",
                children: (
                  <HStack gap={1} align="center">
                    {`${t("set.sortLabel")} · ${t(`set.sort.${sort}`)}`}
                    {/* xs(16px) — sm(20)은 14px 글자 옆에서 글자보다 커 보인다 */}
                    <Icon name="Dropdown" size="xs" />
                  </HStack>
                ),
              }}
              hasChevron={false}
              items={SORT_KEYS.map((key) => ({
                label: t(`set.sort.${key}`),
                // 지금 고른 것에 체크 — 드롭다운은 시트와 달리 눌린 상태를 칩으로
                // 보여줄 자리가 없다.
                icon: sort === key ? <Icon name="Check" size="sm" /> : undefined,
                onClick: () => setSort(key),
              }))}
            />
            {/* 앨범형 ↔ 리스트형. 라벨 대신 아이콘이다 — 격자/줄 모양이 곧 그 뜻이라
                글자보다 빨리 읽히고, 우측 끝에서 자리도 덜 먹는다.
                이름은 aria-label 로 남는다. */}
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value === "list" ? "list" : "album")}
              label={t("set.viewLabel")}
              size="sm"
            >
              <SegmentedControlItem
                value="album"
                label={t("set.viewAlbum")}
                isLabelHidden
                icon={<Icon name="Card" size="sm" />}
              />
              <SegmentedControlItem
                value="list"
                label={t("set.viewList")}
                isLabelHidden
                icon={<Icon name="List" size="sm" />}
              />
            </SegmentedControl>
          </HStack>

          {/* ② 필터 — 값을 고르는 시트를 연다.
              ⚠ dev 의 필터 축은 가격대·레어도 둘뿐이라 가로 스크롤(그늘)이 필요 없다.
                design 의 filterRowWrap 은 다섯 축이 넘칠 때의 장치다. */}
          <HStack gap={1} wrap="wrap">
            {filterButton("price", t("set.filterPrice"))}
            {/* 희귀도가 한 종류뿐이면 필터가 의미 없다 */}
            {rarityOptions.length > 1 && filterButton("rarity", t("set.filterRarity"))}
          </HStack>

          {/* ③ 지금 걸린 조건 — 값마다 칩 하나. 누르면 그것만 풀린다.
              필터를 걸었는지 확인하려고 시트를 다시 열 필요가 없다.
              초기화는 **이 줄 끝**이다 (design 2026-08-28) — 지우는 손잡이는
              지울 것 옆에 있어야 한다. */}
          {activeChips.length > 0 && (
            <HStack gap={1} wrap="wrap" align="center" role="group" aria-label={t("set.activeFilters")}>
              {activeChips.map((c) => (
                <ToggleButton key={c.key} size="sm" data-compact="" label={c.label} isPressed onPressedChange={c.clear}>
                  <HStack gap={1} align="center">
                    {c.label}
                    <Icon name="Close" size="xs" />
                  </HStack>
                </ToggleButton>
              ))}
              <Button
                variant="ghost"
                size="sm"
                /* 칩과 **같은 키**로 선다 (data-compact · global.css).
                   44px 로 두면 32px 칩 줄에서 혼자 커서, 칩을 지우는 손잡이가
                   아니라 그 줄의 주인처럼 보였다. */
                data-compact=""
                label={t("set.clearFilters")}
                clickAction={() => { setRarities([]); setBand(null); }}
              >
                {/* ⚠ 아이콘이 **글자 오른쪽**이다. 칩의 ×(왼쪽 글자 · 오른쪽 아이콘)와
                    같은 순서라 줄 전체가 '글자 뒤에 손잡이' 로 읽힌다.
                    ⚠ 되돌리기(Reload)다 — 지우기(Delete)가 아니다. 필터를 없애는
                      것이 아니라 **처음 상태로 되돌리는** 일이다. */}
                <HStack gap={1} align="center">
                  {t("set.clearFilters")}
                  <Icon name="Reload" size="xs" />
                </HStack>
              </Button>
            </HStack>
          )}

          {/* 결과 수('N장')는 뺐다 (design 2026-08-20). 바로 위 세트 정보에 '수록 카드
              190장 · 보유 2장' 이 이미 있고, 아래로 카드가 그만큼 깔려 있다. 필터로 줄어든
              수는 걸린 칩이 말한다.
              ⚠ 시트 하단('N장' + 적용)의 같은 문구는 남긴다 — 거기서는 지금 고른 조건이
                몇 장을 남기는지가 곧 적용할지 말지의 판단 근거다. */}

          {/* 데이터가 없어 못 거는 축을 밝힌다 — 조용히 빼면 "빠뜨린 것"으로 읽힌다 */}
          <Text type="supporting" color="secondary">{t("set.pendingFilters")}</Text>
        </VStack>

        {/* ── 카드 그리드 ───────────────────────────────────────────── */}
        {cards.isError ? (
          <EmptyState
            title={t("search.loadFailed")}
            description={t("search.loadFailedBody")}
            actions={
              <Button
                variant="secondary"
                label={t("search.retry")}
                clickAction={() => void cards.refetch()}
              />
            }
          />
        ) : cards.isPending ? (
          <Grid gap={3} columns={{ minWidth: ALBUM_MIN_WIDTH, repeat: "fill" }} aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={220} />)}
          </Grid>
        ) : visible.length === 0 ? (
          <EmptyState title={t("set.emptyFiltered")} description={t("set.emptyFilteredBody")} />
        ) : (
          <>
            {/* 데이터는 같고 보는 방식만 다르다. */}
            {viewMode === "list" ? (
              <List hasDividers>
                {/* 인피드 광고 — 13번째부터 12개마다 (수익모델 §3.1 · design 2026-08-27).
                    밀도가 홈(9·10)보다 성긴 것은 이 목록이 한 세트를 훑는 자리라서다 —
                    같은 세트를 보는 동안 광고가 자주 끼면 목록이 끊긴다.
                    ⚠ 앨범형에는 넣지 않는다. 격자 한 칸이 카드 비율(63:88)이라
                      광고가 그 자리에 들어가면 칸이 깨진다. */}
                {withInFeedAds(visible, AD_DENSITY_SET_CARDS, (c) => String(c.cardSerialNo)).map((entry) => {
                  if (entry.kind === "ad") {
                    return <InFeedAd key={entry.key} index={entry.index} />;
                  }
                  const card = entry.item;
                  const price = card.listPrice;
                  const hasPrice = price.display !== "NONE" && price.amount !== null;
                  return (
                    <Item
                      key={card.cardSerialNo}
                      as="li"
                      label={card.name}
                      description={[card.number, card.variantLabel, card.rarity]
                        .filter(Boolean)
                        .join(" · ")}
                      labelLines={1}
                      onClick={() => overlayNavigate(`/cards/${card.cardSerialNo}`)}
                      startContent={
                        <CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} />
                      }
                      endContent={
                        !hasPrice ? (
                          <Text type="supporting" color="secondary">{t("search.noPrice")}</Text>
                        ) : (
                          <Text type="body" weight="bold">
                            {isAuthenticated
                              ? money.format(price.amount ?? 0, price.currency ?? undefined)
                              : maskPrice(price.amount ?? 0)}
                          </Text>
                        )
                      }
                    />
                  );
                })}
              </List>
            ) : (
              <Grid gap={3} columns={{ minWidth: ALBUM_MIN_WIDTH, repeat: "fill" }}>
                {visible.map(renderCell)}
              </Grid>
            )}

            {/* IntersectionObserver 가 없는 환경(테스트·구형 웹뷰)에서도 다음 페이지로 갈 수 있게 */}
            {cards.hasNextPage && (
              <div ref={sentinelRef}>
                <Button
                  variant="secondary"
                  width="fill"
                  isDisabled={cards.isFetchingNextPage}
                  label={cards.isFetchingNextPage ? t("search.loadingMore") : t("search.loadMore")}
                  clickAction={() => void cards.fetchNextPage()}
                />
              </div>
            )}
          </>
        )}
      </VStack>

      {/* ── SC-006a·d·f 바텀시트 ──────────────────────────────────── */}
      <Sheet
        open={openSheet !== null}
        onClose={() => setOpenSheet(null)}
        ariaLabel={openSheet ? sheetTitle[openSheet] : undefined}
        backKind="set-filter-sheet"
      >
        <VStack gap={4} padding={5}>
          <Heading level={3}>
            {openSheet ? sheetTitle[openSheet] : ""}
          </Heading>

          {/* 가격대는 단일 선택 — 서버에 min/max 한 쌍만 보낼 수 있다.
              슬라이더를 두지 않은 이유: 상·하한을 세트 전체 시세에서 뽑아야 한다.
              평균가용 전량 로딩(useCardsetPriceSummary)이 그 값을 쥐고 있으니 이제
              불가능하지는 않지만, 프리셋이면 그 값 없이도 된다 — 바꾸지 않았다. */}
          {openSheet === "price" && (
            <HStack gap={1} wrap="wrap">
              {PRICE_BANDS.map((b) => (
                <ToggleButton
                  key={b.key}
                  label={t(`set.band.${b.key}`)}
                  isPressed={band === b.key}
                  onPressedChange={() => setBand((prev) => (prev === b.key ? null : b.key))}
                >
                  <HStack gap={1} align="center">
                    {t(`set.band.${b.key}`)}
                    {band === b.key && <Icon name="Close" size="xs" />}
                  </HStack>
                </ToggleButton>
              ))}
            </HStack>
          )}

          {openSheet === "rarity" && (
            <HStack gap={1} wrap="wrap">
              {rarityOptions.map((r) => (
                <ToggleButton
                  key={r}
                  label={r}
                  isPressed={rarities.includes(r)}
                  onPressedChange={() => toggleRarity(r)}
                >
                  <HStack gap={1} align="center">
                    {r}
                    {rarities.includes(r) && <Icon name="Close" size="xs" />}
                  </HStack>
                </ToggleButton>
              ))}
            </HStack>
          )}

          {/* 정렬은 다중 선택이 아니라 하나만 고른다 */}
          <Divider />
          <HStack gap={2} justify="between" align="center">
            <Text type="supporting" color="secondary">
              {cards.hasNextPage
                ? t("set.resultCountMore", { count: visible.length })
                : t("set.resultCount", { count: visible.length })}
            </Text>
            <Button
              variant="primary"
              label={t("set.applyFilter")}
              clickAction={() => setOpenSheet(null)}
            />
          </HStack>
        </VStack>
      </Sheet>
    </Section>
  );
}
